import React from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router';

import {Button} from '@edna/components';
import {Stack} from '@edna/components/primitives';
import {formatDate} from '@edna/utils';

import {Link, RouterLink, Text} from 'src/components/primitives';
import {News, NewsDate, StyledNewsDate, useNewsDate} from 'src/containers/News';
import {
  HorizontalNewsContainer,
  NewsBadgePanel,
  NewsBody,
  NewsImage,
  VerticalNewsContainer,
} from 'src/containers/News/NewsContent';
import {
  BlogNewsImage,
  CaseNewsImage,
  ChampionImage,
  MaintenanceImage,
  TariffImage,
} from 'src/images';
import PATHS from 'src/paths';

import {
  ESystemNewsType,
  TBusinessArticleNews,
  TBusinessCaseNews,
  TMessageMatcherPresetNews,
  TReleaseNews,
  TSystemNews,
  TWhatsAppLicensePriceNews,
} from '../model';

type TProps = {
  item: TSystemNews;
};

const UpdatePlanned = React.memo<{item: TReleaseNews}>(({item}) => {
  const {t} = useTranslation();

  const date = useNewsDate(item.meta.startAt);
  const startTime = formatDate(new Date(item.meta.startAt), {format: 'HH:mm'});
  const endTime = formatDate(new Date(item.meta.endAt), {format: 'HH:mm'});

  return (
    <News id={item.id}>
      {({onClose}) => (
        <VerticalNewsContainer>
          <NewsBadgePanel onClose={onClose} />
          <HorizontalNewsContainer>
            <NewsBody title={t(`Notifications:news.${item.type}.title`, {date})} onClose={onClose}>
              <Trans
                i18nKey={`Notifications:news.${item.type}.content`}
                values={{startTime, endTime}}
              >
                <NewsDate date={item.meta.startAt} isOrdinal />
              </Trans>
            </NewsBody>
            <NewsImage image={MaintenanceImage} />
          </HorizontalNewsContainer>
        </VerticalNewsContainer>
      )}
    </News>
  );
});

const WhatsAppLicensePriceUpdatePlannedContent = React.memo<{item: TWhatsAppLicensePriceNews}>(
  ({item}) => {
    return (
      <Trans i18nKey={`Notifications:news.${item.type}.content`}>
        <StyledNewsDate />
        <Link as={RouterLink} to={PATHS.COMPANY_PROFILE_TARIFFS} />
      </Trans>
    );
  },
);

const BusinessBlogContent = React.memo<{item: TBusinessArticleNews; onClose?: TAnyFunction}>(
  ({item, onClose}) => {
    const {t} = useTranslation();

    return (
      <Stack direction="ROW" size="4" isFullWidth>
        <NewsImage image={BlogNewsImage} />
        <Stack size="2" isFullWidth>
          <NewsBadgePanel badgeTitle={t('News:ednaBlog')} onClose={onClose} />
          <Text size="M" weight={500} color="black">
            <Link target="_blank" href={item.meta.url}>
              {item.meta.title}
            </Link>
          </Text>
        </Stack>
      </Stack>
    );
  },
);

const BusinessBlog = React.memo<{item: TBusinessArticleNews}>(({item}) => (
  <News id={item.id} noHighlight>
    {({onClose}) => <BusinessBlogContent item={item} onClose={onClose} />}
  </News>
));

const BusinessCaseContent = React.memo<{item: TBusinessCaseNews; onClose?: TAnyFunction}>(
  ({item, onClose}) => {
    const {t} = useTranslation();

    return (
      <Stack direction="ROW" size="4" isFullWidth>
        <NewsImage image={CaseNewsImage} />
        <Stack size="2" isFullWidth>
          <NewsBadgePanel badgeTitle={t('News:ednaCases')} onClose={onClose} />
          <Text size="M" weight={500} color="black">
            <Link target="_blank" href={item.meta.url}>
              {item.meta.title}
            </Link>
          </Text>
        </Stack>
      </Stack>
    );
  },
);

const BusinessCase = React.memo<{item: TBusinessCaseNews}>(({item}) => (
  <News id={item.id} noHighlight>
    {({onClose}) => <BusinessCaseContent item={item} onClose={onClose} />}
  </News>
));

const WhatsAppLicensePriceUpdatePlanned = React.memo<{item: TWhatsAppLicensePriceNews}>(
  ({item}) => {
    const {t} = useTranslation();

    return (
      <News id={item.id}>
        {({onClose}) => (
          <VerticalNewsContainer>
            <NewsBadgePanel onClose={onClose} />
            <HorizontalNewsContainer>
              <NewsBody title={t(`Notifications:news.${item.type}.title`)} onClose={onClose}>
                <WhatsAppLicensePriceUpdatePlannedContent item={item} />
              </NewsBody>
              <NewsImage image={TariffImage} />
            </HorizontalNewsContainer>
          </VerticalNewsContainer>
        )}
      </News>
    );
  },
);

const MessageMatcherPresetSuggestion = React.memo<{item: TMessageMatcherPresetNews}>(({item}) => {
  const {t} = useTranslation();
  const navigate = useNavigate();

  const count = item.meta?.subjectNames?.length;

  const handleNavigate = React.useCallback(() => {
    navigate(PATHS.MESSAGE_MATCHERS_PRESETS);
  }, []);

  return (
    <News id={item.id}>
      {({onClose}) => (
        <VerticalNewsContainer>
          <NewsBadgePanel onClose={onClose} />
          <HorizontalNewsContainer>
            <NewsBody
              title={t(`Notifications:news.${item.type}.title`, {count})}
              action={
                <Button appearance="primary" onClick={handleNavigate}>
                  {t(`Notifications:news.${item.type}.action`)}
                </Button>
              }
            >
              <Trans
                i18nKey={`Notifications:news.${item.type}.content`}
                values={{
                  channels: item.meta?.subjectNames?.join(', '),
                  count,
                }}
              >
                <StyledNewsDate />
              </Trans>
            </NewsBody>
            <NewsImage image={ChampionImage} />
          </HorizontalNewsContainer>
        </VerticalNewsContainer>
      )}
    </News>
  );
});

const SystemNews = React.memo<TProps>(({item}) => {
  if (!item?.type) {
    return null;
  }

  switch (item.type) {
    case ESystemNewsType.UpdatePlanned:
      return <UpdatePlanned item={item} />;

    case ESystemNewsType.WhatsAppLicensePriceUpdatePlanned:
      return <WhatsAppLicensePriceUpdatePlanned item={item} />;

    case ESystemNewsType.BusinessArticle:
      return <BusinessBlog item={item} />;

    case ESystemNewsType.BusinessCase:
      return <BusinessCase item={item} />;

    case ESystemNewsType.MessageMatcherPresetSuggestion:
      return <MessageMatcherPresetSuggestion item={item} />;

    default:
      return null;
  }
});

SystemNews.displayName = 'SystemNews';

export {
  SystemNews,
  WhatsAppLicensePriceUpdatePlannedContent,
  BusinessCaseContent,
  BusinessBlogContent,
};
